import React from "react";
import type { ApolloClient } from "@apollo/client";
import { connect, type ConnectedProps } from "react-redux";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import { get, size } from "lodash";

import SimpleModal from "components/SimpleModal";
import FileUploader from "components/FileUploader";
import { DOCUMENT } from "statics/docTypes";
import { clearUploadedDocuments } from "actions/userActions";
import { unfreezeEmployer } from "services/adminService";
import type { RootState } from "types/state";
import type {
  FreezeReason,
  UnfreezeEmployerMutationVariables,
} from "types/graphql";

const mapStateToProps = (state: RootState) => {
  return {
    documents: get(state.userDocuments, "documents", []),
  };
};

const mapDispatchToProps = {
  clearUploadedDocuments,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

interface OwnProps {
  // PropTypes had company as a generic object.isRequired; only `id` and
  // `freezeDetails.reason` are read.
  company: {
    id?: UnfreezeEmployerMutationVariables["companyId"];
    freezeDetails?: { reason?: FreezeReason } | null;
  };
  onClose: (hasUploadedDocuments?: boolean) => void;
}

type Props = WithApolloClient<OwnProps & PropsFromRedux>;

const UnfreezeCompanyModal = ({
  onClose,
  company,
  documents,
  client,
}: Props) => {
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string>();

  const closeModal = (hasUploadedDocuments?: boolean) => {
    // found-not-fixed: calls the imported clearUploadedDocuments thunk-creator
    // directly instead of the connected prop (also in mapDispatchToProps), so
    // it is never dispatched and uploaded docs aren't cleared from the store.
    // Preserved verbatim from master; a real fix needs product/QA sign-off.
    clearUploadedDocuments();
    onClose(hasUploadedDocuments);
  };

  const unfreeze = async () => {
    setIsSubmitting(true);
    try {
      const formattedDocuments = documents.map((document) => ({
        // found-not-fixed: reads docUrl/docType off the Document model, which
        // exposes `uri`/`type` — these lodash gets resolve to `undefined` (the
        // keys don't exist), so url/type are always undefined at runtime.
        url: get(document, "docUrl"),
        type: get(document, "docType"),
        contentType: get(document, "contentType"),
        name: get(document, "name"),
      }));
      await unfreezeEmployer(
        client as ApolloClient<object>,
        // lodash get on a known optional key yields `string | number | undefined`
        get(company, "id") as UnfreezeEmployerMutationVariables["companyId"],
        // url/type are `undefined` (see found-not-fixed above) — coerce to the
        // DocumentInput[] shape the service expects, preserving runtime behavior.
        formattedDocuments as unknown as UnfreezeEmployerMutationVariables["uploadedFiles"]
      );
      closeModal(true);
    } catch (err) {
      setError(
        (err as { message?: string })?.message ||
          "An error occurred while unfreezing the company."
      );
    }
    setIsSubmitting(false);
  };

  const hasUploadedDocuments = size(documents) > 0;

  const freezeText = () => {
    const freezeReason = get(company, "freezeDetails.reason", "");
    if (freezeReason === "ACH_RETURN") {
      return "This company was frozen due to an ACH return. Please upload a bank letter to unfreeze.";
    } else {
      return "An approval ticket will be created for review.";
    }
  };

  const shouldRequireDocumentUpload = () => {
    const freezeReason = get(company, "freezeDetails.reason", "");
    return freezeReason === "ACH_RETURN";
  };

  return (
    <SimpleModal
      show={true}
      onClose={closeModal}
      onSubmit={unfreeze}
      title="Unfreeze Company"
      buttonText="Unfreeze"
      closeButtonText="Cancel"
      showCloseButton={true}
      isXhrRequest={true}
      isFetching={isSubmitting}
      btnDisabled={shouldRequireDocumentUpload() && !hasUploadedDocuments}
      error={error}
    >
      <p>{freezeText()}</p>
      <p>
        Any payrolls that ran during the freeze may be in an ERROR state. The
        employer will need to process ad-hoc payrolls for any missed
        contributions.
      </p>
      {shouldRequireDocumentUpload() && (
        <>
          <FileUploader
            fileLimit={3}
            docType={DOCUMENT}
            fileUploadedMsg={
              "Bank letter uploaded successfully. An approval ticket will be created once submitted."
            }
          />
        </>
      )}
    </SimpleModal>
  );
};

export default connector(withApollo<Props>(UnfreezeCompanyModal));
